/**
 * Auth identity helpers — the pure core behind the profile "Bejelentkezés"
 * section and every email-based user lookup.
 *
 * A Sendly account is ONE Firebase Auth user that can carry several sign-in
 * identities, each with its own address: Google, Apple, and email-link. The
 * account's own `email` field is the PRIMARY address — the one shown in the
 * UI and the one we actually send mail to — while `users/{uid}.emails[]` is
 * the searchable index of every address that can sign this person in.
 *
 * Everything here is deliberately pure so it can be unit tested without the
 * Firebase SDK (see src/__tests__/authIdentities.test.js). The address-index
 * core — the provider ids, `normalizeEmail`, `buildEmailIndex`,
 * `sameEmailIndex` — lives in `@sendly/shared` and is re-exported here; the
 * server feeds the same `buildEmailIndex` from an Admin SDK UserRecord in
 * functions/src/authIdentities.js. What stays in this file reads a Firebase
 * `User` or serves the UI.
 */
import { normalizeEmail, PROVIDER_EMAIL, PROVIDER_GOOGLE, PROVIDER_APPLE } from "@sendly/shared";

export { PROVIDER_GOOGLE, PROVIDER_APPLE, PROVIDER_EMAIL, normalizeEmail, buildEmailIndex, sameEmailIndex } from "@sendly/shared";

/**
 * Display order for the identity rows. Email-link comes first because it is
 * the one method that survives a Google/Apple account being closed, so it
 * reads as the fallback the user should set up.
 */
const PROVIDER_ORDER = [PROVIDER_EMAIL, PROVIDER_GOOGLE, PROVIDER_APPLE];

/** Rank for sorting; unknown providers keep their relative order at the end. */
function providerRank(providerId) {
  const i = PROVIDER_ORDER.indexOf(providerId);
  return i === -1 ? PROVIDER_ORDER.length : i;
}

/**
 * The account's linked sign-in identities, ready to render as rows.
 *
 * Exactly one row is flagged `isPrimary`: the first one (in canonical order)
 * whose address matches the account email. Several providers can share the
 * same address, and marking all of them would render the badge two or three
 * times; picking the first keeps it stable across re-renders.
 *
 * @param {?{email: ?string, providerData: ?Array}} user - a Firebase User
 * @returns {Array<{providerId: string, email: ?string, isPrimary: boolean}>}
 */
export function deriveIdentities(user) {
  if (!user || !Array.isArray(user.providerData)) return [];

  const primary = normalizeEmail(user.email);
  const rows = user.providerData
    .filter(Boolean)
    .map(p => ({ providerId: p.providerId, email: normalizeEmail(p.email), isPrimary: false }))
    .sort((a, b) => providerRank(a.providerId) - providerRank(b.providerId));

  if (primary) {
    const first = rows.find(r => r.email === primary);
    if (first) first.isPrimary = true;
  }
  return rows;
}

/**
 * Does this Firebase user carry that sign-in provider?
 *
 * The late-popup check reads the outcome of a "closed" popup from here: the
 * SDK adds the provider to `providerData` before it settles the operation.
 */
export function hasProvider(user, providerId) {
  if (!user || !providerId || !Array.isArray(user.providerData)) return false;
  return user.providerData.some(p => p && p.providerId === providerId);
}

/**
 * May this provider be unlinked?
 *
 * False for the last remaining identity — that unlink would leave the account
 * with no way in at all, which is unrecoverable without admin intervention.
 * Also false for a provider that isn't actually linked.
 */
export function canUnlink(identities, providerId) {
  if (!Array.isArray(identities) || !providerId) return false;
  if (identities.length <= 1) return false;
  return identities.some(i => i && i.providerId === providerId);
}

/*
 * There is deliberately NO `canChangeEmail` gate.
 *
 * An earlier version refused to change the account address unless an
 * email-link identity existed, on the assumption that a federated provider
 * re-syncs its own address into the account at the next sign-in and would
 * revert the change. Testing showed that is false: Firebase leaves the
 * account address alone on federated sign-in. The gate only trapped users on
 * whatever address they happened to have. Don't reintroduce it.
 */

/**
 * Firebase auth error code → i18n key.
 *
 * Returns `null` for the codes that mean "the user backed out" — showing a red
 * error when someone simply closes the provider popup is noise, not feedback.
 */
const AUTH_ERROR_KEYS = {
  "auth/email-already-in-use": "security.errEmailInUse",
  "auth/credential-already-in-use": "security.errCredentialInUse",
  "auth/account-exists-with-different-credential": "security.errCredentialInUse",
  "auth/requires-recent-login": "security.errRecentLogin",
  "auth/no-such-provider": "security.errNotLinked",
  // Shared with the login card — reuse rather than duplicate the wording.
  "auth/invalid-email": "login.emailInvalid",
  "auth/network-request-failed": "login.networkError",
  "auth/too-many-requests": "login.tooManyRequests",
  // User cancelled — not a failure.
  "auth/popup-closed-by-user": null,
  "auth/cancelled-popup-request": null,
};

export function mapAuthError(code) {
  if (typeof code === "string" && code in AUTH_ERROR_KEYS) return AUTH_ERROR_KEYS[code];
  return "security.errGeneric";
}

/**
 * Does this search string look like a COMPLETE email address?
 *
 * Gates the server-side member lookup: a full address is worth a callable
 * round-trip (it can resolve a member's secondary addresses, which never leave
 * the server), while a fragment like `@gmail` stays a local substring filter.
 * Without the "complete" requirement every keystroke on the way to typing an
 * address would fire its own request.
 */
export function isEmailQuery(value) {
  if (typeof value !== "string") return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Local substring match over everything that identifies a person: name,
 * display name, and every address they can sign in with.
 *
 * The haystack concatenates ALL fields rather than picking the first truthy
 * one — the old `name || displayName || email` chain made an email substring
 * unmatchable as soon as a name was set. The primary `email` is in the list on
 * its own account, so a doc with an empty `emails[]` still matches on it.
 */
export function matchesUserSearch(user, query) {
  if (!user) return false;
  const q = typeof query === "string" ? query.trim().toLowerCase() : "";
  if (!q) return true;

  const addresses = Array.isArray(user.emails) ? user.emails : [];
  const haystack = [user.name, user.displayName, user.email, ...addresses]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  return haystack.includes(q);
}
